import { createContext, useCallback, useContext, useState, useMemo, type ReactNode } from 'react'
import { useMailboxes, useComposeEmail } from '../../hooks/useMailboxes'
import { useSuccessToast, useErrorToast } from '../atoms/Toaster'
import { useNavigate } from 'react-router-dom'
import { Send, Loader2, X, AlertCircle, CheckCircle2, Mail, PenSquare, Minimize2, Maximize2 } from 'lucide-react'
import { Button, cn } from '../atoms'
import { AttachmentPicker } from '../molecules/AttachmentPicker'
import { RichTextEditor } from '../molecules/RichTextEditor'
import { EmailAutocomplete } from '../molecules/EmailAutocomplete'
import type { UploadedAttachment } from '../../hooks/useUploadAttachment'

interface ComposeModalContextValue {
  open: () => void
  close: () => void
  isOpen: boolean
}

const ComposeModalContext = createContext<ComposeModalContextValue | null>(null)

export function useComposeModal() {
  const ctx = useContext(ComposeModalContext)
  if (!ctx) throw new Error('useComposeModal debe usarse dentro de <ComposeModalProvider>')
  return ctx
}

export function ComposeModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const value = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen])
  return (
    <ComposeModalContext.Provider value={value}>
      {children}
      {isOpen && <ComposeModal onClose={close} />}
    </ComposeModalContext.Provider>
  )
}

function parseEmailList(input: string): string[] {
  return input
    .split(/[,;\n]+/)
    .map(s => s.trim())
    .filter(Boolean)
}

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)
}

function htmlToText(html: string): string {
  if (!html) return ''
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li|blockquote)>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

function ComposeModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const { data: mailboxes, isLoading: mailboxesLoading } = useMailboxes()
  const compose = useComposeEmail()
  const successToast = useSuccessToast()
  const errorToast = useErrorToast()

  const [resetKey, setResetKey] = useState(0)
  const [mailboxId, setMailboxId] = useState<string>('')
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([])
  const [isMinimized, setIsMinimized] = useState(false)

  const reset = () => {
    setMailboxId('')
    setTo('')
    setCc('')
    setBcc('')
    setSubject('')
    setBody('')
    setShowCc(false)
    setShowBcc(false)
    setAttachments([])
    setResetKey(k => k + 1)
  }

  const defaultMailbox = (() => {
    if (mailboxId) return mailboxes?.find(m => m.id === mailboxId)
    if (!mailboxes || mailboxes.length === 0) return undefined
    return mailboxes[0]
  })()

  const toList = parseEmailList(to)
  const ccList = parseEmailList(cc)
  const bccList = parseEmailList(bcc)
  const invalidTo = toList.filter(e => !isValidEmail(e))
  const invalidCc = showCc ? ccList.filter(e => !isValidEmail(e)) : []
  const invalidBcc = showBcc ? bccList.filter(e => !isValidEmail(e)) : []
  const allRecipients = [...toList, ...ccList, ...bccList]
  const invalidRecipients = [...invalidTo, ...invalidCc, ...invalidBcc]

  const canSend =
    !!defaultMailbox &&
    toList.length > 0 &&
    subject.trim().length > 0 &&
    htmlToText(body).length > 0 &&
    invalidRecipients.length === 0 &&
    !compose.isPending

  const handleSend = async () => {
    if (!defaultMailbox) {
      errorToast('Selecciona una cuenta de envío')
      return
    }
    try {
      await compose.mutateAsync({
        mailboxId: defaultMailbox.id,
        to: toList,
        cc: ccList.length ? ccList : undefined,
        bcc: bccList.length ? bccList : undefined,
        subject: subject.trim(),
        body,
        attachments: attachments.map(a => ({
          id: a.id, url: a.url, filename: a.filename, mimeType: a.mimeType, size: a.size, localPath: a.localPath,
        })),
      })
      successToast('Correo enviado correctamente')
      reset()
      onClose()
      navigate('/emails')
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Error al enviar el correo'
      errorToast(msg)
    }
  }

  const handleClose = () => {
    if (compose.isPending) return
    reset()
    onClose()
  }

  const fromAddress = defaultMailbox?.email || defaultMailbox?.address || ''

  return (
    <div
      className={cn(
        isMinimized
          ? 'fixed inset-0 z-50 pointer-events-none'
          : 'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4'
      )}
      role="dialog"
      aria-modal={!isMinimized}
      aria-labelledby="compose-modal-title"
      onClick={!isMinimized ? handleClose : undefined}
    >
      <div
        className={cn(
          'flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden transition-all',
          isMinimized
            ? 'pointer-events-auto fixed bottom-0 right-4 w-[420px] h-12 rounded-b-none'
            : 'w-full max-w-4xl max-h-[90vh]'
        )}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary-600 flex items-center justify-center">
              <PenSquare className="w-4.5 h-4.5 text-white" aria-hidden="true" />
            </div>
            <div>
              <h2 id="compose-modal-title" className="text-base font-semibold text-gray-900">
                {isMinimized ? 'Redactando correo…' : 'Redactar correo'}
              </h2>
              {!isMinimized && <p className="text-xs text-gray-500">Nuevo mensaje</p>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setIsMinimized(m => !m)}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 transition-colors"
              title={isMinimized ? 'Maximizar' : 'Minimizar'}
              aria-label={isMinimized ? 'Maximizar' : 'Minimizar'}
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            {!isMinimized && (
              <>
                <Button variant="outline" size="sm" onClick={handleClose} disabled={compose.isPending}>
                  <X className="w-4 h-4 mr-1.5" />
                  Descartar
                </Button>
                <Button onClick={handleSend} disabled={!canSend} size="sm">
                  {compose.isPending ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-1.5" />
                  )}
                  Enviar
                </Button>
              </>
            )}
            {isMinimized && (
              <Button onClick={handleSend} disabled={!canSend} size="sm">
                <Send className="w-4 h-4 mr-1.5" />
                Enviar
              </Button>
            )}
          </div>
        </div>

        {/* Body — scrollable */}
        {!isMinimized && (
        <div className="flex-1 overflow-y-auto">
          <div className="border-b border-gray-100">
            <div className="flex items-center gap-2 px-5 py-3">
              <span className="text-sm font-medium text-gray-600 w-14">De</span>
              {mailboxesLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              ) : !mailboxes || mailboxes.length === 0 ? (
                <span className="text-sm text-red-600">No hay cuentas de correo activas. Configúralas en Configuración.</span>
              ) : (
                <select
                  key={`from-${resetKey}`}
                  value={defaultMailbox?.id || ''}
                  onChange={e => setMailboxId(e.target.value)}
                  className="flex-1 text-sm border-0 bg-transparent focus:ring-0 focus:outline-none text-gray-900"
                  aria-label="Cuenta de envío"
                >
                  {mailboxes.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.name} &lt;{m.email || m.address}&gt; {m.department ? `· ${m.department}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="border-b border-gray-100">
            <div className="flex items-start gap-2 px-5 py-3">
              <span className="text-sm font-medium text-gray-600 w-14 pt-1">Para</span>
              <div className="flex-1">
                <EmailAutocomplete
                  key={`to-${resetKey}`}
                  value={to}
                  onChange={setTo}
                  placeholder="Escribe un email o nombre..."
                  ariaLabel="Destinatarios"
                />
                {invalidTo.length > 0 && (
                  <p role="alert" className="text-xs text-red-600 mt-1 inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Emails inválidos: {invalidTo.join(', ')}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-1">
                  {!showCc && (
                    <button onClick={() => setShowCc(true)} className="text-xs text-primary-600 hover:underline">CC</button>
                  )}
                  {!showBcc && (
                    <button onClick={() => setShowBcc(true)} className="text-xs text-primary-600 hover:underline">CCO</button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {showCc && (
            <div className="border-b border-gray-100">
              <div className="flex items-start gap-2 px-5 py-3">
                <span className="text-sm font-medium text-gray-600 w-14 pt-1">CC</span>
                <div className="flex-1">
                  <EmailAutocomplete
                    key={`cc-${resetKey}`}
                    value={cc}
                    onChange={setCc}
                    placeholder="Escribe un email o nombre..."
                    ariaLabel="Con copia"
                  />
                  {invalidCc.length > 0 && (
                    <p role="alert" className="text-xs text-red-600 mt-1">Inválidos: {invalidCc.join(', ')}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {showBcc && (
            <div className="border-b border-gray-100">
              <div className="flex items-start gap-2 px-5 py-3">
                <span className="text-sm font-medium text-gray-600 w-14 pt-1">CCO</span>
                <div className="flex-1">
                  <EmailAutocomplete
                    key={`bcc-${resetKey}`}
                    value={bcc}
                    onChange={setBcc}
                    placeholder="Escribe un email o nombre..."
                    ariaLabel="Con copia oculta"
                  />
                  {invalidBcc.length > 0 && (
                    <p role="alert" className="text-xs text-red-600 mt-1">Inválidos: {invalidBcc.join(', ')}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="border-b border-gray-100">
            <div className="flex items-center gap-2 px-5 py-3">
              <span className="text-sm font-medium text-gray-600 w-14">Asunto</span>
              <input
                key={`subj-${resetKey}`}
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Asunto del correo"
                className="flex-1 text-sm border-0 bg-transparent focus:ring-0 focus:outline-none text-gray-900 placeholder:text-gray-400"
                aria-label="Asunto"
              />
            </div>
          </div>

          <div className="px-5 py-4">
            <RichTextEditor
              key={`body-${resetKey}`}
              value={body}
              onChange={setBody}
              placeholder="Escribe tu mensaje aquí..."
              minHeight="220px"
              disabled={compose.isPending}
              ariaLabel="Cuerpo del mensaje"
            />
          </div>

          <div className="px-5 py-3 border-t border-gray-100">
            <AttachmentPicker value={attachments} onChange={setAttachments} disabled={compose.isPending} label="Adjuntar archivos" />
          </div>
        </div>
        )}

        {/* Footer */}
        {!isMinimized && (
        <div className="px-5 py-2.5 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 flex items-center gap-2 flex-shrink-0">
          <Mail className="w-3.5 h-3.5" />
          <span>
            {allRecipients.length} destinatario{allRecipients.length === 1 ? '' : 's'}
            {attachments.length > 0 && ` · ${attachments.length} adjunto${attachments.length === 1 ? '' : 's'}`}
            {fromAddress && ` · desde ${fromAddress}`}
          </span>
          {canSend && (
            <span className="ml-auto inline-flex items-center gap-1 text-emerald-600">
              <CheckCircle2 className="w-3.5 h-3.5" /> Listo para enviar
            </span>
          )}
        </div>
        )}
      </div>
    </div>
  )
}
